import { AsyncComponent } from '@frontend/components/common';
import { lazy } from 'react';
import { Route } from 'react-router-dom';

import { useSSORoutes } from './account/sso/sso-routes';
import { AttributeRoutes } from './attribute/attribute-routes';
import { BudgetRoutes } from './budget/budget-routes';
import { CertificateRoutes } from './certificate/certificate-routes';
import { ColliRoutes } from './colli/colli-routes';
import { ConstraintRoutes } from './constraint/constraint-routes';
import { CostCenterRoutes } from './cost-center/cost-center-routes';
import { DeviceRoutes } from './device/device-routes';
import { DocumentRoutes } from './document/document-routes';
import { EquipmentGroupRoutes } from './equipment-group/equipment-group-routes';
import { EquipmentProductRoutes } from './equipment-product/equipment-product-routes';
import { AdminIdentificationRoutes, IdentificationRoutes } from './identification/identification-routes';
import { ImportRoutes } from './import/import-routes';
import { IOTRoutes } from './iot/iot-routes';
import { MovementRoutes } from './movement/movement-routes';
import { NotificationRoutes } from './notification/notification-routes';
import { PackageRoutes } from './package/package-routes';
import { PlaylistRoutes } from './playlist/playlist-routes';
import { ProblemRoutes } from './problem/problem-routes';
import { ProductCategoryRoutes } from './product-category/product-category-routes';
import { ProductGroupRoutes } from './product-group/product-group-routes';
import { ProductKitRoutes } from './product-kit/product-kit-routes';
import { ProductRoutes } from './product/product-routes';
import { QuestionGroupRoutes } from './question-group/question-group-routes';
import { AnswerRoutes } from './question/answer/answer-routes';
import { QuestionRoutes } from './question/question-routes';
import { ReviewRoutes } from './review/review-routes';
import { CreateRoleRoutes } from './role/create-role-routes';
import { SettingRoutes } from './setting/setting-routes';
import { SpotRoutes } from './spot/spot-routes';
import { StockRoutes } from './stock/stock-routes';
import { TagRoutes } from './tag/tag-routes';
import { TransactionRoutes } from './transaction/transaction-routes';
import { UserBadgeRoutes } from './user-badge/user-badge-routes';
import { UserGroupRoutes } from './user-group/user-group-routes';
import { UserRoutes } from './user/user-routes';

const Configuration = lazy(() => import('./configuration/configuration.component'));
const Troubleshoot = lazy(() => import('./troubleshoot/troubleshoot.component'));
const PermissionsList = lazy(() => import('./permissions/permissions-list.component'));

export function AdminRoutes() {
    const AnswerRoute = AnswerRoutes();
    const AttributeRoute = AttributeRoutes();
    const BudgetRoute = BudgetRoutes();
    const CertificateRoute = CertificateRoutes();
    const CostCenterRoute = CostCenterRoutes();
    const CreateRoleRoute = CreateRoleRoutes();
    const DeviceRoute = DeviceRoutes();
    const DocumentRoute = DocumentRoutes();
    const EquipmentRoute = EquipmentProductRoutes();
    const EquipmentGroupRoute = EquipmentGroupRoutes();
    const IdentificationRoute = IdentificationRoutes();
    const AdminIdentificationRoute = AdminIdentificationRoutes();
    const ImportRoute = ImportRoutes();
    const IoTRoutes = IOTRoutes();
    const MovementRoute = MovementRoutes();
    const NotificationRoute = NotificationRoutes();
    const ColliRoute = ColliRoutes();
    const PackageRoute = PackageRoutes();
    const PlaylistRoute = PlaylistRoutes();
    const ProblemRoute = ProblemRoutes();
    const ProductCategoryRoute = ProductCategoryRoutes();
    const ProductRoute = ProductRoutes();
    const ProductKitRoute = ProductKitRoutes();
    const ProductGroupRoute = ProductGroupRoutes();
    const QuestionRoute = QuestionRoutes();
    const QuestionGroupRoute = QuestionGroupRoutes();
    const ReviewRoute = ReviewRoutes();
    const SpotRoute = SpotRoutes();
    const SSORoutes = useSSORoutes();
    const StockRoute = StockRoutes();
    const TagRoute = TagRoutes();
    const TransactionRoute = TransactionRoutes();
    const UserBadgeRoute = UserBadgeRoutes();
    const UserRoute = UserRoutes();
    const UserGroupRoute = UserGroupRoutes();
    const settingRoutes = SettingRoutes();
    const ConstraintRoute = ConstraintRoutes();

    return (
        <Route path='admin'>
            {settingRoutes}
            <Route
                index
                element={
                    <AsyncComponent>
                        <Configuration />
                    </AsyncComponent>
                }
            />
            <Route path='accounts'>
                <Route path=':accountId'>
                    {SSORoutes}

                    {/**
         // #region Account Entities
        * */}
                    <Route path=':entityType'>
                        <Route path=':entityId'>
                            <Route
                                path='permissions'
                                element={<PermissionsList />}
                            />
                            <Route
                                path='debug'
                                element={<Troubleshoot />}
                            />
                            {CreateRoleRoute}

                            {IdentificationRoute}
                        </Route>
                    </Route>
                    {/**
         // #region Account user groups
        * */}
                    {UserGroupRoute}
                    {/**
         // #region Account equipment groups
        * */}
                    {EquipmentGroupRoute}
                    {/**
         // #region Account product groups
        * */}
                    {ProductGroupRoute}

                    {/**
            // #region Account budgets
        * */}
                    {BudgetRoute}
                    {/**
            // #region Account documents
        * */}
                    {DocumentRoute}
                    {/**
         // #region Account cost centers
        * */}
                    {CostCenterRoute}
                    {/**
                 // #region Account  tags
                * */}
                    {TagRoute}
                    {/**
         // #region Account playlists
        * */}
                    {PlaylistRoute}
                    {/**
         // #region Account question groups
        * */}
                    {QuestionGroupRoute}
                    {/**
         // #region Account questions
        * */}
                    {QuestionRoute}
                    {/**
        // #region Account spots
        * */}
                    {SpotRoute}
                    {/**
         // #region Account equipment
        * */}
                    {EquipmentRoute}
                    {/**
         // #region Account users
        * */}
                    {UserRoute}
                    {/**
         // #region Account products
        * */}
                    {ProductRoute}
                    {ProductKitRoute}
                    {/**
         // #region Account attributes
        * */}
                    {AttributeRoute}

                    {/**
         // #region Account Certificates
        * */}
                    {CertificateRoute}
                    {/**
         // #region Account IoTs
        * */}
                    {IoTRoutes}
                    {/**
                     * // #region Account Problems
                     */}
                    {ProblemRoute}
                    {/**
         // #region Account Transactions
        * */}
                    {TransactionRoute}
                    {/**
              // #region Account Notification
             * */}
                    {NotificationRoute}
                    {/**
         // #region Account product categories
        * */}
                    {ProductCategoryRoute}

                    {ConstraintRoute}

                    {ReviewRoute}

                    {ImportRoute}
                    {UserBadgeRoute}
                    {AdminIdentificationRoute}
                </Route>
            </Route>
            {/**
 // #region User groups
* */}
            {UserGroupRoute}
            {/**
 // #region Equipment groups
* */}
            {EquipmentGroupRoute}
            {/**
 // #region Product groups
* */}
            {ProductGroupRoute}

            {/**
 // #region Cost centers
* */}
            {CostCenterRoute}
            {/**
    // #region Budgets
* */}
            {BudgetRoute}
            {/**
    // #region Document
* */}
            {DocumentRoute}
            {/**
        // #region  Tags
        * */}
            {TagRoute}
            {/**
 // #region Question groups
* */}
            {QuestionGroupRoute}
            {/**
    // #region Questions
* */}
            {QuestionRoute}
            {/**
    // #region Answers
* */}
            {AnswerRoute}
            {/*
    // #region Playlists
* */}
            {PlaylistRoute}
            {/*
    // #region Spots
* */}
            {SpotRoute}
            {/*
    // #region Equipment
* */}
            {EquipmentRoute}
            {/*
    // #region Transactions
* */}
            {TransactionRoute}
            {/*
    // #region Users
* */}
            {UserRoute}
            {AdminIdentificationRoute}
            {/*
    // #region Products
* */}
            {ProductRoute}
            {ProductKitRoute}
            {/*
    // #region Reviews
* */}
            {ReviewRoute}
            {/*
    // #region Attributes
* */}
            {AttributeRoute}
            {/*
    // #region Certificates
* */}
            {CertificateRoute}
            {/*
    // #region IoT
* */}
            {IoTRoutes}

            {/*
    // #region Problems
* */}
            {ProblemRoute}
            {/*
    // #region Devices
* */}
            {DeviceRoute}
            {/*
    // #region Stocks
* */}
            {StockRoute}
            {/*
    // #region Movements
* */}
            {MovementRoute}
            {/*
    // #region Packages
* */}
            {PackageRoute}
            {/*
    // #region Collis
* */}
            {ColliRoute}

            {ImportRoute}
            {/** 
     // #region Notifications
     */}
            {NotificationRoute}
            {/** 
     // #region Product Categories
     */}
            {ProductCategoryRoute}

            {ConstraintRoute}

            <Route path=':entityType'>
                <Route path=':entityId'>
                    <Route
                        path='permissions'
                        element={<PermissionsList />}
                    />
                    <Route
                        path='debug'
                        element={<Troubleshoot />}
                    />
                </Route>
            </Route>

            {UserBadgeRoute}
        </Route>
    );
}
